import { FC, useMemo } from 'react'
import { CCPayment } from 'components/features/creditCard/creditCardStore/types'
import { capitalizeFirstLetter, formatUSCurrency } from 'helpers/formatHelpers'
import moment from 'moment'
import { DATE_FORMAT_READABLE } from 'constants/datetime'
import { DeleteAutoPaymentActionButton, DeletePaymentActionButton } from 'components/features/pfm/Button/ActionButtons'
import Flex from 'components/atoms/Flex/Flex'
import { BACK_BUTTON_PROPS, CardPaymentsPaneButton, PayCardConfirmPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import { currentCCLedgerSelector, deleteAutoPaymentRequestSelector, deletePaymentRequestSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { getAutoPaymentAmount, getPaymentAmountTypeTitle, getPaymentMethodTitle } from 'components/features/creditCard/creditCardStore/helpers'

const CardPaymentItem: FC<CCPayment> = (props) => {
  const { uid, payment_method, amount, payment_amount_type, status } = props
  const isOneTimePayment = props.schedule_type === 'one-time'
  const isPaymentComplete = status !== 'pending' && status !== 'created'
  const deletePaymentRequest = useStoreSelector(deletePaymentRequestSelector)
  const deleteAutoPayRequest = useStoreSelector(deleteAutoPaymentRequestSelector)
  const ledger = useStoreSelector(currentCCLedgerSelector)

  // Does Not use isOneTimePayment to avoid typsecript error
  const date = useMemo(() => props.schedule_type === 'one-time' ? (
    moment(props.payment_date).format(DATE_FORMAT_READABLE)
  ) : (
    props.days_before_due_date + ' before due date'
  ), [props])

  const cancelPaymentButton = useMemo(() => isOneTimePayment && !isPaymentComplete && (
    <DeletePaymentActionButton
      size='mini'
      text='Cancel This Payment'
      theme='danger'
      style='textOnly'
      uid={ uid }
      isSubmitting={ deletePaymentRequest.isLoading }
    />
  ), [isOneTimePayment, isPaymentComplete, uid, deletePaymentRequest.isLoading])

  const autoPayButtons = useMemo(() => !isOneTimePayment && !isPaymentComplete && (
    <>
      <PayCardConfirmPaneButton
        size='small'
        text='Edit'
        theme='lightBlue'
        backButton={ <CardPaymentsPaneButton { ...BACK_BUTTON_PROPS } /> }
        isAutoPay
      />
      <DeleteAutoPaymentActionButton
        size='tiny'
        text='Turn off Auto-pay'
        style='textOnly'
        theme='danger'
        isSubmitting={ deleteAutoPayRequest.isLoading }
      />
    </>
  ), [isOneTimePayment, isPaymentComplete, deleteAutoPayRequest.isLoading])

  const primaryValue = useMemo(() => isOneTimePayment ? (
    formatUSCurrency(amount, 'penny')
  ) : (
    getPaymentAmountTypeTitle(payment_amount_type)
  ), [amount, isOneTimePayment, payment_amount_type])

  const secondaryValue = useMemo(() => isOneTimePayment ? (
    capitalizeFirstLetter(status)
  ) : (
    getAutoPaymentAmount(payment_amount_type, ledger)
  ), [status, isOneTimePayment, payment_amount_type, ledger])

  return (
    <Flex component='article' theme='card' gap='tiny' padding='defaultSize' rounded>
      <div>
        <Flex component='label' theme='darkCyan' themeStyle='textOnly'>{ date }</Flex>
        <Flex component='p' horizontal gap='spread'>
          { isOneTimePayment ? 'One Time Payment' : 'Auto Payment' }
          <strong>{ primaryValue }</strong>
        </Flex>
        <Flex horizontal gap='spread'>
          <small>From { getPaymentMethodTitle(payment_method) + ' ending in ' + payment_method?.number_last_digits }</small>
          <small>{ secondaryValue }</small>
        </Flex>
      </div>
      <Flex gap='tiny'>
        { cancelPaymentButton || autoPayButtons }
      </Flex>
    </Flex>
  )
}

export default CardPaymentItem
